import { useEffect, useCallback } from 'react'
import { useHistory, useParams } from 'react-router-dom'
import { Box } from 'theme-ui'
import { Alert, Loading, Nav } from 'components'
import {
  GlobalUpdateUserNotification,
  useAuth,
  useConfigUtils,
} from 'providers'
import { StudentFormContentLoader } from 'components/UserForm/StudentFormContentLoader'
import { useUpdate, useUser } from 'hooks/userHooks'
import getLocalized from 'i18n'
import { UserProfile } from 'components/UserProfile/UserProfile'
import { TEACHER_UPDATE, STUDENT_UPDATE, TRAINING_UPDATE } from '../constants'
import { UserUpdateFormValues } from 'types'
import * as access from 'types/access'

const localized = getLocalized()

export function Update() {
  const { profile, logout, hasAccess } = useAuth()
  useEffect(() => {
    document.title = localized.documentTitles.adminDashboard
  }, [])
  const history = useHistory()
  const utils = useConfigUtils()
  const { id } = useParams<{ id: string }>()
  const [user, readState] = useUser(id, true)
  const [updateState, update] = useUpdate()
  const readError = readState.matches('failed') ? readState.context.error : ''
  const updateError = updateState.matches('failed')
    ? updateState.context.error
    : ''
  const loading = readState.matches('syncing') || updateState.matches('syncing')

  async function handleLogoutClick() {
    await logout(profile.email)
    history.push({
      pathname: '/',
      state: undefined,
    })
  }
  const isCustomerSupportUser = profile.groups.includes('SupportGroup')

  const handleFormSubmit = useCallback(
    (formData: Partial<UserUpdateFormValues>) => {
      update({ id, ...formData })
    },
    [update, id]
  )

  const goBack = useCallback(() => {
    history.push({ pathname: '/', state: null })
  }, [history])

  const canCreateStudents =
    hasAccess(access.Groups.DASHBOARD, access.Dashboard.CREATE_TEST_USERS) ||
    hasAccess(access.Groups.DASHBOARD, access.Dashboard.CREATE_USERS)

  const canViewHelpCenter = hasAccess(
    access.Groups.DASHBOARD,
    access.Dashboard.VIEW_HELP_CENTER
  )

  const canViewDropdownDate = hasAccess(
    access.Groups.DASHBOARD,
    access.Dashboard.NO_DATE_OF_INSTRUCTION_RESTRICTION
  )

  return (
    <Box
      data-cy="update-page"
      sx={{
        bg: 'whitesmoke',
        display: 'flex',
        flexDirection: 'column',
        minHeight: '100vh',
      }}
    >
      <Nav
        profile={profile}
        onLogoutClick={handleLogoutClick}
        access={{
          createStudent: canCreateStudents,
          createTeacher: isCustomerSupportUser,
          viewHelpCenter: canViewHelpCenter,
        }}
      />
      <Box
        data-cy="user-profile"
        sx={{
          width: '100vw',
          flexDirection: 'column',
          flex: '1 0 auto',
          alignItems: 'center',
          bg: 'white',
          pt: 42,
          position: 'relative',
        }}
      >
        {readState.matches('syncing') ? (
          <Box
            style={{
              position: 'absolute',
              top: 12,
              left: 7,
              zIndex: 10000,
              width: '6rem',
              height: '2rem',
            }}
          >
            <Loading />
          </Box>
        ) : null}

        {readError ? null : !user || !utils ? (
          <Box variant="userProfile.form">
            <StudentFormContentLoader />
          </Box>
        ) : (
          <UserProfile
            user={user}
            configUtils={utils}
            isSupport={isCustomerSupportUser}
            responseError={updateError}
            pageContext={
              user?.userType === 'Teacher'
                ? TRAINING_UPDATE
                : user?.userType === 'TeacherDC' && isCustomerSupportUser
                ? TEACHER_UPDATE
                : STUDENT_UPDATE
            }
            loading={loading}
            onLeaveForm={goBack}
            onSubmitForm={handleFormSubmit}
            access={{
              viewDropdownDate: canViewDropdownDate,
            }}
          />
        )}
      </Box>
      <GlobalUpdateUserNotification />

      {readError && (
        <Alert variant="info" onClose={goBack} autoClose={false} open>
          {readError}
        </Alert>
      )}
    </Box>
  )
}
